import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useWishlist } from '../context/WishlistContext';
import { useProducts } from '../context/ProductContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Wishlist = () => {
  const { wishlist, removeFromWishlist, loading: wishlistLoading } = useWishlist();
  const { products, loading: productsLoading } = useProducts();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Get wishlist products
  const wishlistProducts = products.filter(product => wishlist.includes(product.id));

  const handleRemove = async (productId) => {
    const result = await removeFromWishlist(productId);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  const handleAddToCart = (product) => {
    const defaultSize = product.sizes?.[0];
    if (defaultSize) {
      addToCart(product, defaultSize);
      toast.success(`${product.name} added to cart!`);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
        <div className="container mx-auto px-4 text-center">
          <Heart className="w-24 h-24 mx-auto text-purple-500 mb-6" />
          <h1 className="text-4xl font-bold text-white mb-4">My Wishlist</h1>
          <p className="text-purple-200 mb-8">Please sign in to view your wishlist</p>
          <Link to="/auth">
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (wishlistLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20 flex items-center justify-center">
        <Sparkles className="w-12 h-12 text-yellow-400 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-purple-300 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <Heart className="w-12 h-12 mx-auto mb-4 text-yellow-400 fill-yellow-400" />
          <h1 className="text-4xl font-bold text-white mb-2">My Wishlist</h1>
          <p className="text-purple-200">
            {wishlistProducts.length} {wishlistProducts.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>

        {wishlistProducts.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-24 h-24 mx-auto text-purple-500 mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">Your wishlist is empty</h2>
            <p className="text-purple-200 mb-8">Save your favorite products for later!</p>
            <Link to="/products">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-purple-900">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlistProducts.map(product => (
              <Card 
                key={product.id} 
                className="bg-purple-800/40 border-purple-600 overflow-hidden group"
                data-testid={`wishlist-item-${product.id}`}
              >
                <Link to={`/product/${product.id}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4 bg-yellow-500 text-purple-900 px-3 py-1 rounded-full text-sm font-semibold">
                      ${product.basePrice?.toFixed(2)}
                    </div>
                  </div>
                </Link>
                <CardContent className="pt-4">
                  <Link to={`/product/${product.id}`}>
                    <p className="text-purple-300 text-sm mb-1">{product.category}</p>
                    <h3 className="text-lg font-semibold text-white mb-2 hover:text-yellow-400 transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-purple-200 text-sm line-clamp-2 mb-4">{product.description}</p>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-purple-900"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Add to Cart
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleRemove(product.id)}
                      className="border-red-500 text-red-400 hover:bg-red-500/20"
                      data-testid={`remove-wishlist-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
